// packages import
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react'
import { Box, Typography, useMediaQuery } from '@mui/material'
import { fromZonedTime } from 'date-fns-tz'
import { Event } from 'microsoft-graph'
import moment from 'moment'
import { useContext, useEffect, useState } from 'react'
import { Calendar, EventProps, View, momentLocalizer } from 'react-big-calendar'
import "react-big-calendar/lib/css/react-big-calendar.css"
import Marquee from "react-fast-marquee"
import { ThreeCircles } from 'react-loader-spinner'
import { findIana } from 'windows-iana'
// component import
import { CalenderEventModal } from './CalenderEventModal'
// others
import { getUserWeekCalendar } from '../../../../graphService'
import { OutlookLogo, images } from "../../../assets/images"
import { AppContext } from '../../../context'
import { ActionType } from '../../../context/AppContextReducer'
import { RBEvent } from '../../../interfaceTypes'
import palette from '../../../theme/palette'
import { BlurBox, OutlookSignInButton } from '../../../theme/styleComponents'

export const CalendarEvents = () => {
  const { outlookUser, outlookSignIn, outlookAuthProvider, outlookDisplayError, dispatch } = useContext(AppContext);
  const [events, setEvents] = useState<Event[]>();
  const { timeZone } = outlookUser || {}
  const ianaTimeZones = findIana(timeZone!);
  const defaultStartDateTime = ianaTimeZones && fromZonedTime(moment().startOf('month').format('YYYY-MM-DD HH:mm'), ianaTimeZones[0]?.valueOf()).toISOString();
  const defaultEndDateTime = ianaTimeZones && fromZonedTime(moment().endOf('month').endOf('day').format('YYYY-MM-DD HH:mm'), ianaTimeZones[0]?.valueOf()).toISOString();
  const [startDateTime, setStartDateTime] = useState<string>(defaultStartDateTime);
  const [endDateTime, setEndDateTime] = useState<string>(defaultEndDateTime);
  const [loadingEvents, setLoadingEvents] = useState<boolean>(false);
  const localizer = momentLocalizer(moment)
  const mediumViewport = useMediaQuery('(max-width:768px)');
  const allViews: View[] = ['month', 'week', 'day', 'agenda'];

  useEffect(() => {
    const loadEvents = async () => {
      setLoadingEvents(true)

      if (outlookUser) {
        try {
          const ianaTimeZones = findIana(timeZone!);
          const events = await getUserWeekCalendar(outlookAuthProvider!, ianaTimeZones[0].valueOf(), startDateTime!, endDateTime!);
          setEvents(events);
          setLoadingEvents(false)
        } catch (err) {
          const error = err as Error;
          outlookDisplayError!(error.message);
          setLoadingEvents(false)
        }
      }
    };

    loadEvents();
    // eslint-disable-next-line
  }, [outlookUser, startDateTime, endDateTime, timeZone]);

  const finalEvents = events && events.map((item) => {
    const { id, subject, start, end, attendees, organizer } = item || {}
    const { dateTime: startDateTime } = start || {}
    const { dateTime: endDateTime } = end || {}

    return {
      id: id,
      title: subject,
      start: moment(startDateTime).toDate(),
      end: moment(endDateTime).toDate(),
      attendees: attendees,
      organizer: organizer
    }
  })

  const handleSelectedEvent = (event: RBEvent) => {
    dispatch({ type: ActionType.SET_IS_OPEN, isOpen: true })
    dispatch({ type: ActionType.SET_CURRENT_EVENT, currentEvent: event })
  }

  const handleNavigate = (newDate: Date) => {
    const selectedDate = moment(newDate).startOf('month').format('YYYY-MM-DD')

    setStartDateTime(ianaTimeZones && fromZonedTime(moment(selectedDate).startOf('month').format('YYYY-MM-DD hh:mm'), ianaTimeZones[0]?.valueOf()).toISOString())

    setEndDateTime(ianaTimeZones && fromZonedTime(moment(selectedDate).endOf('month').format('YYYY-MM-DD hh:mm'), ianaTimeZones[0]?.valueOf()).toISOString())
  }

  const renderEvent = ({ event }: EventProps<RBEvent>): JSX.Element => (
    <Marquee gradientWidth={0}>{event?.title}</Marquee>
  )

  return (
    <Box mt={5} mb={2} overflow='auto'>
      <Box position='relative'>
        <UnauthenticatedTemplate>
          <BlurBox>
            <Box textAlign='center' maxWidth='400px' minHeight='700px' display='flex' alignItems='center'>
              <Box>
                <Box component='img' src={images.OUTLOOK_EMPTY} />

                <Typography fontSize='32px' fontWeight='700'>Connect to Outlook</Typography>

                <Typography fontSize='16px' color='#8F8F8F' mt="10px" mb='20px'>
                  To see your events, we’ll first need to connect with your outlook account!
                </Typography>

                <OutlookSignInButton startIcon={<OutlookLogo />} variant='outlined' onClick={outlookSignIn!}>
                  Sign in with Outlook
                </OutlookSignInButton>
              </Box>
            </Box>
          </BlurBox>
        </UnauthenticatedTemplate>

        <AuthenticatedTemplate>
          <Box position='relative'>
            {loadingEvents &&
              <BlurBox sx={{ position: 'absolute' }}>
                <ThreeCircles
                  height="100"
                  width="100"
                  color={palette.primary.main}
                  visible={true}
                  ariaLabel="three-circles-rotating"
                />
              </BlurBox>
            }

            <Calendar
              popup
              selectable
              views={allViews}
              endAccessor="end"
              events={finalEvents}
              startAccessor="start"
              localizer={localizer}
              defaultView='month'
              onNavigate={handleNavigate}
              onSelectEvent={(event) => handleSelectedEvent(event)}
              style={{ height: 700, width: mediumViewport ? '700px' : '100%', overflow: 'auto' }}
              components={{
                event: renderEvent,
              }}
              eventPropGetter={(event) => {
                const { title } = event || {}
                const isEvent = typeof title === "string" ? title?.split('--')[1] : undefined;
                const isEventTitle = isEvent === ' Event '
                const isCourtTitle = isEvent === ' Court Date '
                const eventColors = isEventTitle ? '#771117' : isCourtTitle ? '#ffc8ce' : '#187711'

                return {
                  style: {
                    backgroundColor: eventColors,
                    color: 'white',
                    borderColor: eventColors,
                  }
                }
              }}
            />
          </Box>

          <CalenderEventModal />

        </AuthenticatedTemplate>
      </Box>
    </Box>
  )
}